/**
 * Formatação de datas única dos módulos financeiros.
 * Não reimplementar formatDateBr/formatDateIso nos módulos.
 */

export function formatDateIso(date: Date = new Date()): string {
  const mes = String(date.getMonth() + 1).padStart(2, "0");
  const dia = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${mes}-${dia}`;
}

export function formatMonthIso(date: Date = new Date()): string {
  const mes = String(date.getMonth() + 1).padStart(2, "0");
  return `${date.getFullYear()}-${mes}`;
}

export function formatDateBr(
  value?: string | Date,
  emptyLabel = "-",
): string {
  if (!value) return emptyLabel;
  const date =
    typeof value === "string" && value.length === 10
      ? new Date(`${value}T00:00:00`)
      : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === "string" ? value : emptyLabel;
  }
  return date.toLocaleDateString("pt-BR");
}

export function formatDateTimeBr(value?: string | Date): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("pt-BR");
}

export function getTodayIso(): string {
  return formatDateIso(new Date());
}

export function getTodayBr(): string {
  const hoje = new Date();
  const mes = String(hoje.getMonth() + 1).padStart(2, "0");
  const dia = String(hoje.getDate()).padStart(2, "0");
  return `${dia}/${mes}/${hoje.getFullYear()}`;
}

/** Data e hora atuais em PT-BR: dd/mm/aaaa HH:mm:ss */
export function getNowDateTimeBr(date: Date = new Date()): string {
  const dia = String(date.getDate()).padStart(2, "0");
  const mes = String(date.getMonth() + 1).padStart(2, "0");
  const hora = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  const seg = String(date.getSeconds()).padStart(2, "0");
  return `${dia}/${mes}/${date.getFullYear()} ${hora}:${min}:${seg}`;
}

export function maskDateBr(value: string): string {
  const digits = (value || "").replace(/\D/g, "").slice(0, 8);
  return digits
    .replace(/(\d{2})(\d)/, "$1/$2")
    .replace(/(\d{2})(\d)/, "$1/$2");
}

/** Converte dd/mm/yyyy -> yyyy-mm-dd. Retorna "" se vazio e null se inválido. */
export function parseBrDateToIso(value: string): string | null {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";

  const match = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;

  const [, dd, mm, yyyy] = match;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return `${yyyy}-${mm}-${dd}`;
}

/** Converte yyyy-mm-dd (ou ISO completo) -> dd/mm/yyyy. */
export function isoToBrDate(value?: string): string {
  if (!value) return "";
  const iso = String(value).slice(0, 10);
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const [, yyyy, mm, dd] = match;
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Data e hora curtas (dd/MM HH:mm) de um registro cuja data foi gravada pelo
 * banco (`criado_em`/`atualizado_em` com DEFAULT current_timestamp).
 *
 * O MariaDB grava essas colunas no horário de Brasília e o TypeORM
 * (`timezone: 'Z'` no ormconfig) devolve o mesmo relógio de parede com sufixo
 * Z. Logo a hora de Brasília está nos componentes UTC do instante e NÃO pode
 * passar por conversão para o fuso do navegador (mostraria 3 h a menos).
 * Se a plataforma unificar o fuso do banco um dia, este é o único lugar a mudar.
 */
export function formatDbDateTimeShort(
  value?: string | Date | null,
  emptyLabel = "-"
): string {
  if (!value) return emptyLabel;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return emptyLabel;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)} ${pad(
    date.getUTCHours()
  )}:${pad(date.getUTCMinutes())}`;
}
